"use client"

import { addToCart, cartHasLineItemForVariant } from "@lib/data/cart"
import { notifyCartChanged } from "@lib/context/cart-context"
import { useTracking } from "@lib/analytics/tracking-context"
import { reportClientError } from "@lib/util/client-error-report"
import type { SiteCareTemplate, SiteStoreLocator } from "@lib/data/site-manager"
import { getProductPrice } from "@lib/util/get-product-price"
import { getDisplayTitle } from "@lib/util/get-display-title"
import { getProductStyle } from "@lib/util/get-product-style"
import { isNewProduct } from "@lib/util/new-product-badge"
import SaleTag from "@modules/common/components/sale-tag"
import ColBadge from "@modules/brand/ColBadge"
import { HttpTypes } from "@medusajs/types"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import type { SizeChart } from "@modules/size-chart/lib/fetch-size-chart"
import { Dialog, Transition } from "@headlessui/react"
import Image from "@modules/common/components/cdn-image"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { Fragment, useEffect, useMemo, useRef, useState } from "react"
import {
  getVariantStockMessage,
  isOutOfStockVariant,
  isSelectableVariant,
} from "./product-stock"
import {
  MOBILE_PRODUCT_PAGE_PADDING_CLASS,
  MOBILE_PRODUCT_STICKY_BOTTOM_CLASS,
} from "@modules/layout/utils/mobile-fixed-offsets"
import {
  buildProductStoryItems,
  buildStoreStockRows,
  getSelectedColorLabel,
  getSizeChartMeasurement,
} from "./product-page-data"
import { replaceVariantUrlWithoutNavigation } from "./product-variant-url"
import { getFriendlyCartErrorMessage } from "./product-cart-actions"
import { recoverFromStaleDeploy } from "@lib/util/stale-deploy"

type ProductTemplateProps = {
  product: HttpTypes.StoreProduct
  region: HttpTypes.StoreRegion
  countryCode: string
  images: HttpTypes.StoreProductImage[]
  defaultSize?: string
  sizeChart?: SizeChart | null
  careTemplates?: SiteCareTemplate[]
  relatedProducts?: HttpTypes.StoreProduct[]
  storeLocator?: SiteStoreLocator
}

type IconProps = { className?: string }
type OptionMap = Record<string, string | undefined>

const SIZE_ORDER = ["XS", "S", "M", "L", "XL", "2XL", "XXL", "3XL", "4XL", "5XL"]

const normalize = (value?: string | null) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "")

const optionTitle = (option: HttpTypes.StoreProductOption) =>
  String(option.title || "").toLowerCase()

const getSizeRank = (value?: string | null) => {
  const index = SIZE_ORDER.indexOf(normalize(value))
  return index === -1 ? SIZE_ORDER.length : index
}

const optionsAsKeymap = (
  variantOptions: HttpTypes.StoreProductVariant["options"]
) =>
  variantOptions?.reduce((acc: Record<string, string>, option) => {
    if (option.option_id) {
      acc[option.option_id] = option.value
    }

    return acc
  }, {})

const optionValueFor = (
  variant: HttpTypes.StoreProductVariant,
  optionId?: string
) => variant.options?.find((option) => option.option_id === optionId)?.value

const findOption = (product: HttpTypes.StoreProduct, keyword: string) =>
  product.options?.find((option) => optionTitle(option).includes(keyword))

const getVariantOptionValues = (
  variants: HttpTypes.StoreProductVariant[],
  optionId: string
) => {
  const seen = new Set<string>()
  const values: string[] = []

  variants.forEach((variant) => {
    const value = optionValueFor(variant, optionId)
    const key = normalize(value)
    if (value && !seen.has(key)) {
      seen.add(key)
      values.push(value)
    }
  })

  return values
}

const sortOptionValues = (
  option: HttpTypes.StoreProductOption,
  values: string[]
) => {
  if (!optionTitle(option).includes("size")) {
    return values
  }

  return [...values].sort((a, b) => {
    const rank = getSizeRank(a) - getSizeRank(b)
    if (rank !== 0) return rank
    return a.localeCompare(b)
  })
}

const hasSameOptions = (
  variant: HttpTypes.StoreProductVariant,
  options: OptionMap
) => {
  const variantOptions = optionsAsKeymap(variant.options) || {}
  const keys = Object.keys(variantOptions)

  return (
    keys.length === 0 ||
    keys.every((key) => normalize(variantOptions[key]) === normalize(options[key]))
  )
}

const getDefaultVariant = (
  product: HttpTypes.StoreProduct,
  preferredSize = "M",
  queryVariantId?: string | null
) => {
  const variants = product.variants || []
  const queryVariant = queryVariantId
    ? variants.find((variant) => variant.id === queryVariantId)
    : undefined

  if (queryVariant) {
    return queryVariant
  }

  const sizeOptionId = findOption(product, "size")?.id
  const preferred = normalize(preferredSize)
  const sorted = variants
    .map((variant, index) => ({
      index,
      variant,
      rank: getSizeRank(optionValueFor(variant, sizeOptionId)),
    }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)

  return (
    sorted.find(
      ({ variant }) =>
        isSelectableVariant(variant) &&
        normalize(optionValueFor(variant, sizeOptionId)) === preferred
    )?.variant ||
    sorted.find(({ variant }) => isSelectableVariant(variant))?.variant ||
    sorted[0]?.variant
  )
}

const formatBdt = (amount?: number | null) => {
  if (typeof amount !== "number") return ""
  return new Intl.NumberFormat("en-BD", {
    style: "currency",
    currency: "BDT",
    maximumFractionDigits: 0,
  }).format(amount)
}

const splitList = (value?: unknown, fallback: string[] = []) => {
  if (Array.isArray(value)) {
    return value.map(String).filter(Boolean)
  }

  if (typeof value === "string" && value.trim()) {
    return value
      .split(/\n|;|\|/)
      .map((item) => item.trim())
      .filter(Boolean)
  }

  return fallback
}

const getMetaNumber = (meta: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = meta[key]
    const parsed = typeof value === "number" ? value : Number(value)
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed
    }
  }

  return null
}

const formatInlineMeasurement = (value: string, unit: string) => {
  if (!value || value === "-") return "-"
  if (/("|cm|inch|inches)$/i.test(value)) return value
  if (unit === "inch") return `${value}"`
  return `${value} ${unit}`
}

const storeBadgeClass = (status: "in" | "low" | "out") => {
  if (status === "out") return "bg-red-50 text-red-700"
  if (status === "low") return "bg-alif-warn/10 text-alif-warn"
  return "bg-alif-success/10 text-alif-success"
}

const getSwatchClass = (value: string) => {
  const color = value.toLowerCase()
  if (color.includes("black") || color.includes("onyx") || color.includes("noir")) {
    return "bg-alif-ink"
  }
  if (color.includes("white")) return "bg-white"
  if (color.includes("cream") || color.includes("ivory") || color.includes("ecru")) {
    return "bg-alif-paper"
  }
  if (color.includes("green") || color.includes("emerald")) return "bg-emerald-900"
  if (color.includes("blue") || color.includes("navy")) return "bg-blue-950"
  if (color.includes("maroon") || color.includes("wine")) return "bg-red-950"
  if (color.includes("gold")) return "bg-alif-gold"
  return "bg-alif-muted"
}

const SearchIcon = ({ className = "h-4 w-4" }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className={className} aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.7-3.7" />
  </svg>
)

const BagIcon = ({ className = "h-4 w-4" }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className={className} aria-hidden="true">
    <path d="M6 8h12l-1 12H7L6 8Z" />
    <path d="M9 8a3 3 0 0 1 6 0" />
  </svg>
)

const TruckIcon = ({ className = "h-4 w-4" }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className={className} aria-hidden="true">
    <path d="M3 7h11v10H3z" />
    <path d="M14 10h4l3 3v4h-7z" />
    <circle cx="7" cy="18" r="1.7" />
    <circle cx="18" cy="18" r="1.7" />
  </svg>
)

const ExchangeIcon = ({ className = "h-4 w-4" }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
    <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
    <path d="M16 21v-5h5" />
  </svg>
)

const DiamondIcon = ({ className = "h-4 w-4" }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M6 3h12l4 6-10 13L2 9Z" />
    <path d="M11 3 8 9l4 13 4-13-3-6" />
    <path d="M2 9h20" />
  </svg>
)

const PinIcon = ({ className = "h-4 w-4" }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className={className} aria-hidden="true">
    <path d="M12 21s7-6.4 7-12a7 7 0 1 0-14 0c0 5.6 7 12 7 12Z" />
    <circle cx="12" cy="9" r="2.3" />
  </svg>
)

const RulerIcon = ({ className = "h-4 w-4" }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className={className} aria-hidden="true">
    <path d="m4 17 13-13 3 3L7 20l-3-3Z" />
    <path d="m8 13 2 2M11 10l2 2M14 7l2 2" />
  </svg>
)

const CloseIcon = ({ className = "h-5 w-5" }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className={className} aria-hidden="true">
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
)

const ArrowIcon = ({ className = "h-4 w-4" }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className={className} aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
)

const StarIcon = ({ className = "h-4 w-4" }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="m12 2.5 2.8 6 6.5.8-4.8 4.5 1.2 6.4L12 17l-5.7 3.2 1.2-6.4-4.8-4.5 6.5-.8L12 2.5Z" />
  </svg>
)

const StarOrnament = ({ className = "h-4 w-4" }: IconProps) => (
  <svg viewBox="0 0 32 32" fill="none" className={className} aria-hidden="true">
    <path
      d="M16 2 20 12 30 16 20 20 16 30 12 20 2 16 12 12 16 2Z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <path
      d="m16 8 3.2 4.8L24 16l-4.8 3.2L16 24l-3.2-4.8L8 16l4.8-3.2L16 8Z"
      stroke="currentColor"
      strokeWidth="1.1"
      strokeLinejoin="round"
    />
  </svg>
)

const Pill = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex w-fit items-center rounded-full border border-alif-green px-4 py-2 text-[9px] font-semibold uppercase tracking-[0.28em] text-alif-green">
    {children}
  </span>
)

const OrnamentDivider = () => (
  <div className="mx-auto flex w-full max-w-md items-center justify-center gap-4 text-alif-gold">
    <span className="h-px flex-1 bg-alif-gold" />
    <StarOrnament className="h-4 w-4" />
    <span className="h-1.5 w-1.5 rotate-45 border border-alif-gold" />
    <StarOrnament className="h-4 w-4" />
    <span className="h-px flex-1 bg-alif-gold" />
  </div>
)

const ModalShell = ({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
}) => (
  <Transition appear show={open} as={Fragment}>
    <Dialog as="div" className="relative z-[100]" onClose={onClose}>
      <Transition.Child
        as={Fragment}
        enter="duration-200 ease-out"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="duration-150 ease-in"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 bg-alif-ink/55" />
      </Transition.Child>

      {/* Bottom sheet on mobile (anchored to the bottom edge, slides up),
          centered dialog on desktop (scale + fade). */}
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-end justify-center small:items-center small:px-4 small:py-8">
          <Transition.Child
            as={Fragment}
            enter="transition duration-300 ease-out"
            enterFrom="translate-y-full opacity-100 small:translate-y-0 small:scale-95 small:opacity-0"
            enterTo="translate-y-0 opacity-100 small:scale-100 small:opacity-100"
            leave="transition duration-200 ease-in"
            leaveFrom="translate-y-0 opacity-100 small:scale-100 small:opacity-100"
            leaveTo="translate-y-full opacity-100 small:translate-y-0 small:scale-95 small:opacity-0"
          >
            <Dialog.Panel className="flex max-h-[90vh] w-full flex-col overflow-hidden rounded-t-2xl border border-alif-rule bg-alif-paper text-alif-ink shadow-[0_-8px_30px_rgba(26,22,18,0.18)] small:max-h-[85vh] small:max-w-3xl small:rounded-lg small:shadow-xl">
              {/* Mobile grab handle */}
              <div className="flex shrink-0 justify-center pt-2.5 small:hidden">
                <span
                  className="h-1 w-10 rounded-full bg-alif-rule"
                  aria-hidden="true"
                />
              </div>
              <div className="flex shrink-0 items-center justify-between border-b border-alif-rule px-5 py-4">
                <Dialog.Title className="font-display text-xl small:text-2xl">
                  {title}
                </Dialog.Title>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex h-9 w-9 items-center justify-center rounded-sm border border-alif-rule text-alif-ink transition hover:border-alif-green hover:text-alif-green small:h-10 small:w-10"
                  aria-label={`Close ${title}`}
                >
                  <CloseIcon />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto pb-[env(safe-area-inset-bottom)] small:pb-0">
                {children}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </div>
    </Dialog>
  </Transition>
)

export default function ProductTemplate({
  product,
  countryCode,
  images,
  defaultSize = "M",
  sizeChart,
  careTemplates = [],
  relatedProducts = [],
  storeLocator,
}: ProductTemplateProps) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const heroRef = useRef<HTMLDivElement>(null)
  // Derive the initial size/variant selection DURING the first render (server +
  // client) instead of in a post-hydration effect. Previously options started {}
  // so the buy-box first painted with no size selected, then the effect below
  // filled it in — growing the column and shoving the footer down (mobile CLS
  // ~0.93). getDefaultVariant is deterministic (URL v_id → preferred size "M"),
  // so server and client compute the same initial state → no reflow, no
  // hydration mismatch. The effect is kept only to react to later URL changes.
  const [options, setOptions] = useState<OptionMap>(
    () =>
      optionsAsKeymap(
        getDefaultVariant(product, defaultSize, searchParams.get("v_id"))?.options
      ) || {}
  )
  const [imageIndex, setImageIndex] = useState(0)
  const [isAdding, setIsAdding] = useState(false)
  const [isBuying, setIsBuying] = useState(false)
  const [justAdded, setJustAdded] = useState(false)
  const [cartActionMessage, setCartActionMessage] = useState<string | null>(null)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false)
  const [storeOpen, setStoreOpen] = useState(false)
  const [stickyVisible, setStickyVisible] = useState(false)

  const sizeOption = findOption(product, "size")
  const colorOption = findOption(product, "color") || findOption(product, "colour")

  // Customer-facing name (Odoo "Online Title" → metadata.display_name), falling
  // back to the raw Medusa title. Used for every visible name + alt text.
  const displayTitle = getDisplayTitle(product)
  const [hydratedAt, setHydratedAt] = useState<Date | null>(null)
  const showNewBadge = hydratedAt ? isNewProduct(product, hydratedAt) : false

  useEffect(() => {
    setHydratedAt(new Date())
  }, [])

  useEffect(() => {
    const defaultVariant = getDefaultVariant(
      product,
      defaultSize,
      searchParams.get("v_id")
    )

    if (defaultVariant) {
      setOptions(optionsAsKeymap(defaultVariant.options) || {})
    }
  }, [product, defaultSize, searchParams])

  const selectedVariant = useMemo(
    () => product.variants?.find((variant) => hasSameOptions(variant, options)),
    [product.variants, options]
  )

  useEffect(() => {
    setJustAdded(false)
  }, [selectedVariant?.id])

  const { trackViewItem, trackAddToCart } = useTracking()

  // view_item — fire once per product (not on every variant switch). Report
  // product.variants[0] EXPLICITLY, decoupled from `options`: before the CLS fix
  // above, `options` was empty at mount so this resolved to variants[0]. Now that
  // options is pre-seeded with the default size, reading it here would change the
  // GA4/Meta payload (item_id/item_variant). Keeping variants[0] preserves the
  // exact tracking behavior. (verified: view_item still fires once, same item.)
  useEffect(() => {
    trackViewItem(product as any, product.variants?.[0] as any)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id])

  const isValidVariant = useMemo(
    () =>
      Boolean(
        product.variants?.some((variant) => hasSameOptions(variant, options))
      ),
    [product.variants, options]
  )

  useEffect(() => {
    const value = isValidVariant ? selectedVariant?.id : null
    replaceVariantUrlWithoutNavigation({ pathname, variantId: value })
  }, [selectedVariant, isValidVariant, pathname])

  useEffect(() => {
    const onScroll = () => {
      const bottom = heroRef.current?.getBoundingClientRect().bottom || 0
      setStickyVisible(bottom < 120)
    }

    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  useEffect(() => {
    if (!lightboxOpen) return
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setLightboxOpen(false)
      if (event.key === "ArrowRight") {
        setImageIndex((current) => Math.min(current + 1, displayImages.length - 1))
      }
      if (event.key === "ArrowLeft") {
        setImageIndex((current) => Math.max(current - 1, 0))
      }
    }
    document.body.classList.add("overflow-hidden")
    window.addEventListener("keydown", handleKey)
    return () => {
      document.body.classList.remove("overflow-hidden")
      window.removeEventListener("keydown", handleKey)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxOpen])

  const selectedSize = sizeOption ? options[sizeOption.id] || defaultSize : defaultSize
  const selectedColor = getSelectedColorLabel(colorOption, options)
  const fit = getSizeChartMeasurement(sizeChart, selectedSize)
  const inStock = isSelectableVariant(selectedVariant)
  const stockMessage = getVariantStockMessage(selectedVariant)

  const price = useMemo(() => {
    const { cheapestPrice, variantPrice } = getProductPrice({
      product,
      variantId: selectedVariant?.id,
    })
    const selectedPrice = selectedVariant ? variantPrice : cheapestPrice

    if (!selectedPrice) return ""
    return selectedPrice.currency_code?.toLowerCase() === "bdt"
      ? formatBdt(selectedPrice.calculated_price_number)
      : selectedPrice.calculated_price
  }, [product, selectedVariant])

  // Sale info: when the variant is on a "sale" price list, expose the original
  // (struck-through) price + discount % so the PDP can show the markdown.
  const priceInfo = useMemo(() => {
    const { cheapestPrice, variantPrice } = getProductPrice({
      product,
      variantId: selectedVariant?.id,
    })
    const selectedPrice = selectedVariant ? variantPrice : cheapestPrice
    if (!selectedPrice) return null

    const isBdt = selectedPrice.currency_code?.toLowerCase() === "bdt"
    const isSale =
      selectedPrice.price_type === "sale" &&
      (selectedPrice.original_price_number ?? 0) >
        (selectedPrice.calculated_price_number ?? 0)
    return {
      isSale,
      original: isBdt
        ? formatBdt(selectedPrice.original_price_number)
        : selectedPrice.original_price,
      percentage: selectedPrice.percentage_diff,
    }
  }, [product, selectedVariant])

  const meta = (product.metadata || {}) as Record<string, unknown>
  const productCode =
    selectedVariant?.sku || product.external_id || product.handle || product.id
  const productDescription = product.description || ""
  const details = splitList(meta.details)
  const materials = splitList(meta.materials)
  const careTemplateId =
    typeof meta.careTemplateId === "string" ? meta.careTemplateId : ""
  const selectedCareTemplate = careTemplates.find(
    (template) => template.id === careTemplateId
  )
  const care = splitList(selectedCareTemplate?.content || meta.care)
  const specs = [
    ["Details", details],
    ["Materials", materials],
    ["Care", care],
  ].filter(([, items]) => (items as string[]).length > 0)
  const reviewAverage = getMetaNumber(meta, [
    "review_average",
    "reviewAverage",
    "rating",
  ])
  const reviewCount = getMetaNumber(meta, [
    "review_count",
    "reviewCount",
    "reviews_count",
  ])
  const hasReviewSummary = Boolean(reviewAverage && reviewCount)
  const reviewHighlights = splitList(meta.review_highlights || meta.reviews)
  const explicitStoryTitle =
    typeof meta.story_title === "string"
      ? meta.story_title
      : typeof meta.storyTitle === "string"
      ? meta.storyTitle
      : ""
  const explicitStoryAccent =
    typeof meta.story_accent === "string"
      ? meta.story_accent
      : typeof meta.storyAccent === "string"
      ? meta.storyAccent
      : ""
  const storyPoints = splitList(meta.story_points || meta.storyPoints)
  const storyTitle = explicitStoryTitle || "Crafted for Everyday Elegance"
  const storyAccent = explicitStoryAccent || productDescription
  const storyItems = buildProductStoryItems({
    storyItems: meta.story_items || meta.storyItems,
    storyPoints,
    details,
    materials,
    care,
  })
  const hasStorySection = Boolean(
    explicitStoryTitle ||
      explicitStoryAccent ||
      productDescription ||
      storyItems.length
  )

  const filteredImages = useMemo(() => {
    if (!selectedVariant?.images?.length) return images
    const variantImageIds = new Set(selectedVariant.images.map((image) => image.id))
    const filtered = images.filter((image) => variantImageIds.has(image.id))
    return filtered.length ? filtered : images
  }, [images, selectedVariant])

  const displayImages = filteredImages.length
    ? filteredImages
    : product.thumbnail
    ? [{ id: "thumbnail", url: product.thumbnail }]
    : []
  const sizeGuideColumns = sizeChart?.columns || []
  const sizeGuideLabelColumn =
    sizeGuideColumns.find((column) => column.type === "label") ||
    sizeGuideColumns[0]

  const variantForOptions = (nextOptions: OptionMap) =>
    product.variants?.find((variant) => hasSameOptions(variant, nextOptions))

  const setOptionValue = (optionId: string, value: string) => {
    setOptions((current) => ({ ...current, [optionId]: value }))
  }

  const handleSubmit = async (checkout = false) => {
    if (!selectedVariant?.id) return

    setCartActionMessage(null)

    if (checkout) {
      setIsBuying(true)
    } else {
      setIsAdding(true)
    }

    try {
      setJustAdded(false)
      if (checkout && (await cartHasLineItemForVariant(selectedVariant.id))) {
        router.push("/checkout?step=address")
        return
      }

      await addToCart({
        variantId: selectedVariant.id,
        quantity: 1,
        countryCode,
      })
      trackAddToCart(product as any, selectedVariant as any, 1)
      notifyCartChanged()
      if (checkout) {
        router.push("/checkout?step=address")
      } else {
        setJustAdded(true)
        window.setTimeout(() => setJustAdded(false), 3000)
      }
    } catch (error) {
      // A stale open tab from before a redeploy fails here with an unknown
      // Server Action id; reload once onto the current build instead of showing
      // the generic "could not update your bag" (which just makes the shopper
      // retry the same dead action).
      if (recoverFromStaleDeploy(error)) return
      const message = getFriendlyCartErrorMessage(error)
      setCartActionMessage(message)
      reportClientError({
        source: "cart-action",
        error,
        extra: {
          countryCode,
          productHandle: product.handle || null,
          productId: product.id,
          selectedVariantId: selectedVariant.id,
          variantSku: selectedVariant.sku || null,
        },
      })
    } finally {
      if (checkout) {
        setIsBuying(false)
      } else {
        setIsAdding(false)
      }
    }
  }

  const canSubmit =
    Boolean(selectedVariant?.id && isValidVariant && inStock) &&
    !isAdding &&
    !isBuying

  const storeRows = buildStoreStockRows({
    locations: storeLocator?.locations || [],
    selectedVariant,
    variants: product.variants || [],
  })

  const relatedCards = relatedProducts.slice(0, 5)

  return (
    <section className={`bg-alif-ivory text-alif-ink ${MOBILE_PRODUCT_PAGE_PADDING_CLASS}`}>
      <nav className="border-b border-alif-rule bg-alif-paper" aria-label="Breadcrumb">
        <ol className="mx-auto flex max-w-[1320px] items-center gap-1 overflow-x-auto whitespace-nowrap px-4 py-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-alif-muted small:gap-2 small:px-10 small:py-4 small:tracking-[0.25em]">
          <li className="shrink-0">
            <LocalizedClientLink href="/" className="transition hover:text-alif-green">
              Home
            </LocalizedClientLink>
          </li>
          <li className="shrink-0" aria-hidden="true">/</li>
          {product.collection ? (
            <>
              <li className="max-w-[42vw] shrink-0 truncate small:max-w-none">
                <LocalizedClientLink
                  href={`/collections/${product.collection.handle}`}
                  className="transition hover:text-alif-green"
                >
                  {product.collection.title}
                </LocalizedClientLink>
              </li>
              <li className="shrink-0" aria-hidden="true">/</li>
            </>
          ) : null}
          <li className="min-w-0 flex-1 truncate text-alif-ink" aria-current="page">
            {displayTitle}
          </li>
        </ol>
      </nav>

      <div
        ref={heroRef}
        className="mx-auto grid max-w-[1320px] gap-8 px-4 py-10 small:px-8 large:grid-cols-[minmax(0,1.05fr)_minmax(390px,0.95fr)] large:px-10 large:py-12"
      >
        {/* Clarity's Balanced masking hides images, which blanks the garment
            out of every PDP recording — the one thing worth watching for on a
            clothing store. Product photography is public catalogue content, so
            this subtree (thumbnails + main image) is explicitly unmasked. Do
            NOT lift this to a shared image component: it would also unmask
            anything rendered on /checkout or /account. */}
        <div
          data-clarity-unmask="true"
          className="grid gap-4 min-[640px]:grid-cols-[96px_minmax(0,1fr)] min-[640px]:gap-[18px]"
        >
          <div className="order-2 flex gap-3 overflow-x-auto pb-1 min-[640px]:order-1 min-[640px]:flex-col min-[640px]:overflow-visible min-[640px]:pb-0">
            {displayImages.slice(0, 3).map((image, index) => (
              <button
                type="button"
                key={image.id || image.url || index}
                onClick={() => setImageIndex(index)}
                className={`relative h-[92px] w-[74px] shrink-0 bg-alif-ivory-deep min-[640px]:h-[120px] min-[640px]:w-24 ${
                  imageIndex === index
                    ? "outline outline-2 outline-offset-[3px] outline-alif-green"
                    : "outline outline-1 outline-alif-rule"
                }`}
                aria-label={`View image ${index + 1}`}
              >
                {image.url ? (
                  <Image
                    src={image.url}
                    alt={`${displayTitle} thumbnail ${index + 1}`}
                    fill
                    optimize
                    sizes="96px"
                    className="object-contain object-center"
                  />
                ) : null}
              </button>
            ))}
          </div>

          <div className="order-1 min-[640px]:order-2">
            <div
              className="relative mx-auto w-full max-w-[560px] border border-alif-gold bg-alif-paper p-3 before:absolute before:inset-[6px] before:border before:border-alif-gold before:content-[''] large:max-w-[640px]"
            >
              <div
                className="group relative z-[1] aspect-[4/5] w-full cursor-zoom-in overflow-hidden bg-alif-paper"
                onClick={() => setLightboxOpen(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault()
                    setLightboxOpen(true)
                  }
                }}
                aria-label="Open image lightbox"
              >
                {/* Deliberately NOT `optimize` — this is the shot the customer
                    judges fabric, weave and embroidery on. The masters are only
                    945x1181 (~72-174KB), so at this element's size `fit=scale-down`
                    never removes a pixel: an edge transform would only re-encode
                    at q80, i.e. a second lossy pass over an already-lossy JPEG,
                    to save ~50KB. Serving the master raw from the CDN is both
                    higher quality and immune to the transform quota (see
                    lib/image/cloudflare-loader.ts). Same reasoning as the
                    lightbox below. Keep `optimize` on the thumbnails/cards,
                    where it genuinely discards invisible pixels. */}
                {displayImages[imageIndex]?.url ? (
                  <Image
                    src={displayImages[imageIndex].url}
                    alt={`${displayTitle} main image`}
                    fill
                    priority
                    sizes="(max-width: 1024px) 100vw, 760px"
                    className="object-contain object-center transition duration-200 group-hover:opacity-95"
                  />
                ) : null}
                <StarOrnament className="pointer-events-none absolute hidden small:block left-2 top-2 z-10 h-4 w-4 text-alif-gold drop-shadow-[0_1px_2px_rgba(255,255,255,0.75)]" />
                <StarOrnament className="pointer-events-none absolute hidden small:block right-2 top-2 z-10 h-4 w-4 text-alif-gold drop-shadow-[0_1px_2px_rgba(255,255,255,0.75)]" />
                <StarOrnament className="pointer-events-none absolute hidden small:block bottom-2 left-2 z-10 h-4 w-4 text-alif-gold drop-shadow-[0_1px_2px_rgba(255,255,255,0.75)]" />
                <StarOrnament className="pointer-events-none absolute hidden small:block bottom-2 right-2 z-10 h-4 w-4 text-alif-gold drop-shadow-[0_1px_2px_rgba(255,255,255,0.75)]" />
                {showNewBadge && (
                  <div className="absolute right-4 top-4 z-10" aria-label="new">
                    <ColBadge variant="new" />
                  </div>
                )}
                <div className="absolute bottom-4 left-4 rounded-full bg-alif-paper/85 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-alif-ink">
                  {imageIndex + 1} / {Math.max(displayImages.length, 1)}
                </div>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation()
                    setLightboxOpen(true)
                  }}
                  className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-full bg-alif-paper/90 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-alif-ink transition hover:text-alif-green"
                >
                  <SearchIcon className="h-3.5 w-3.5" />
                  Zoom
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="large:sticky large:top-28 large:self-start">
          <div className="relative border-y border-alif-rule py-8">
            <div className="pointer-events-none absolute right-0 top-4 hidden font-arabic text-[120px] leading-none text-alif-gold/10 large:block">
              ا
            </div>
            <h1 className="font-display text-[42px] font-normal leading-none text-alif-ink small:text-[56px]">
              {displayTitle}
            </h1>
            <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-alif-muted">
              Style № {productCode}
              {selectedColor ? ` · ${selectedColor}` : ""}
            </p>
            <div className="mt-7 flex flex-col gap-5 border-b border-alif-rule pb-6 small:flex-row small:items-end small:justify-between">
              <div>
                <p className="flex items-baseline gap-3 font-display text-4xl leading-none">
                  <span className={priceInfo?.isSale ? "text-[#b85c00]" : ""}>
                    {price}
                  </span>
                  {priceInfo?.isSale && (
                    <span className="text-2xl text-alif-muted line-through">
                      {priceInfo.original}
                    </span>
                  )}
                </p>
                <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-alif-muted">
                  BDT · incl. VAT
                  {priceInfo?.isSale && priceInfo.percentage ? (
                    <span className="ml-2 text-[#b85c00]">
                      · Save {priceInfo.percentage}%
                    </span>
                  ) : null}
                </p>
              </div>
              {hasReviewSummary ? (
              <div className="text-left small:text-right">
                <div className="flex gap-1 text-alif-gold small:justify-end">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <StarIcon key={index} className="h-4 w-4" />
                  ))}
                </div>
                <p className="mt-2 text-[11px] font-medium text-alif-muted">
                  {reviewAverage?.toFixed(1)} · {reviewCount} reviews
                </p>
              </div>
              ) : null}
            </div>

            <div className="mt-7 space-y-7">
              {colorOption ? (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-alif-muted">
                  Colour <span className="text-alif-ink">{selectedColor}</span>
                </p>
                <div className="mt-4 flex gap-3">
                  {getVariantOptionValues(product.variants || [], colorOption.id).map(
                    (value) => {
                      const active =
                        normalize(options[colorOption.id]) === normalize(value)
                      const nextOptions = { ...options, [colorOption.id]: value }
                      const disabled = !variantForOptions(nextOptions)

                      return (
                        <button
                          type="button"
                          key={value}
                          disabled={disabled}
                          onClick={() => setOptionValue(colorOption.id, value)}
                          className={`group relative h-9 w-9 rounded-full border border-alif-rule p-1 transition disabled:opacity-40 ${
                            active ? "ring-2 ring-alif-green ring-offset-2" : ""
                          }`}
                          aria-label={`Select ${value}`}
                        >
                          <span
                            className={`block h-full w-full rounded-full ${getSwatchClass(
                              value
                            )}`}
                          />
                          <span className="pointer-events-none absolute -top-9 left-1/2 hidden -translate-x-1/2 whitespace-nowrap bg-alif-ink px-2 py-1 text-[10px] text-alif-paper group-hover:block">
                            {value}
                          </span>
                        </button>
                      )
                    }
                  )}
                </div>
              </div>
              ) : null}

              {sizeOption ? (
                <div>
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-alif-muted">
                      Size <span className="text-alif-ink">{selectedSize}</span>
                    </p>
                    <button
                      type="button"
                      onClick={() => setSizeGuideOpen(true)}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-alif-green underline underline-offset-4"
                    >
                      <RulerIcon className="h-3.5 w-3.5" />
                      Size guide
                    </button>
                  </div>
                  <div className="mt-4 flex border border-alif-ink">
                    {sortOptionValues(
                      sizeOption,
                      getVariantOptionValues(product.variants || [], sizeOption.id)
                    ).map((value, index, values) => {
                      const active = normalize(selectedSize) === normalize(value)
                      const nextOptions = { ...options, [sizeOption.id]: value }
                      const variant = variantForOptions(nextOptions)
                      const unavailable = isOutOfStockVariant(variant)
                      const disabled = !variant

                      return (
                        <button
                          type="button"
                          key={value}
                          disabled={disabled}
                          onClick={() => setOptionValue(sizeOption.id, value)}
                          aria-label={`${value}${unavailable ? " out of stock" : ""}`}
                          title={unavailable ? `${value} is out of stock` : undefined}
                          className={`relative min-w-0 flex-1 overflow-hidden px-1 py-4 text-center text-[12px] font-semibold uppercase tracking-[0.14em] transition disabled:cursor-not-allowed disabled:bg-alif-rule disabled:text-alif-muted ${
                            index < values.length - 1 ? "border-r border-alif-ink" : ""
                          } ${
                            active
                              ? "bg-alif-ink text-alif-paper"
                              : unavailable
                                ? "bg-alif-paper text-alif-muted hover:bg-alif-ivory-deep"
                                : "bg-alif-paper text-alif-ink hover:bg-alif-ivory-deep"
                          }`}
                        >
                          <span className="relative z-10">{value}</span>
                          {unavailable ? (
                            <span
                              aria-hidden="true"
                              className="pointer-events-none absolute left-1/2 top-1/2 h-px w-[140%] -translate-x-1/2 -translate-y-1/2 -rotate-[28deg] bg-current opacity-70"
                            />
                          ) : null}
                        </button>
                      )
                    })}
                  </div>
                  {fit ? (
                    <div className="mt-[10px] grid gap-3 border border-alif-rule bg-alif-paper px-4 py-[14px] text-[12px] text-alif-muted small:grid-cols-3">
                      <div>
                        <span className="font-semibold text-alif-ink">{fit.size}</span>{" "}
                        · measurements
                      </div>
                      {fit.values.slice(0, 2).map((measurement) => (
                        <div key={measurement.label}>
                          {measurement.label}{" "}
                          {formatInlineMeasurement(measurement.value, measurement.unit)}
                        </div>
                      ))}
                    </div>
                  ) : null}
                  <div className="mt-[10px] flex items-center gap-2 text-[12px] font-medium text-alif-muted">
                    <span className="h-2 w-2 rounded-full bg-alif-success" />
                    {stockMessage?.tone === "out"
                      ? "Out of stock"
                      : "In stock · ships from Dhaka"}
                  </div>
                </div>
              ) : null}

              <div className="space-y-[10px]">
                {cartActionMessage ? (
                  <div
                    role="status"
                    className="border border-alif-green bg-alif-green/10 px-4 py-3 text-[12px] font-medium leading-5 text-alif-ink"
                  >
                    {cartActionMessage}
                  </div>
                ) : null}
                <button
                  type="button"
                  onClick={() => handleSubmit(false)}
                  disabled={!canSubmit}
                  className={`flex w-full items-center justify-center gap-2 px-6 py-[18px] text-[12px] font-semibold uppercase tracking-[0.25em] text-alif-paper transition disabled:cursor-not-allowed disabled:opacity-50 ${
                    justAdded
                      ? "animate-pulse bg-alif-green hover:bg-alif-green"
                      : "bg-alif-ink hover:bg-black"
                  }`}
                  data-testid="add-product-button"
                >
                  <BagIcon className="h-4 w-4" />
                  {isAdding ? "Adding" : justAdded ? "Added to bag" : "Add to bag"}
                </button>
                <button
                  type="button"
                  onClick={() => handleSubmit(true)}
                  disabled={!canSubmit}
                  className="w-full bg-alif-green px-6 py-[18px] text-[12px] font-semibold uppercase tracking-[0.25em] text-alif-paper transition hover:bg-alif-green-deep hover:text-alif-paper disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isBuying ? "Preparing checkout" : "Buy now"}
                </button>
                <button
                  type="button"
                  onClick={() => setStoreOpen(true)}
                  className="w-full border border-alif-ink px-6 py-4 text-[12px] font-semibold uppercase tracking-[0.22em] transition hover:border-alif-green hover:text-alif-green"
                >
                  Find in store
                </button>
              </div>

              <div className="grid gap-3 border border-[#5C6B53] bg-[#EEF3EC] p-4 small:grid-cols-3">
                {[
                  ["Reliable Shipping", "Fast delivery nationwide to your doorstep", TruckIcon],
                  ["Hassle-Free Exchange", "7 days to swap your size", ExchangeIcon],
                  ["Signature Collection", "Authentic premium fabric", DiamondIcon],
                ].map(([label, sublabel, Icon]) => (
                  <div key={String(label)} className="flex items-start gap-3">
                    <Icon className="mt-0.5 h-4 w-4 text-[#3D5240]" />
                    <div>
                      <p className="text-[11px] font-semibold text-[#3D5240]">
                        {label as string}
                      </p>
                      <p className="mt-1 text-[10px] text-[#3D5240]">
                        {sublabel as string}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {productDescription ? (
                <p className="font-display text-[19px] italic leading-8 text-alif-ink">
                  “{productDescription}”
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {specs.length ? (
      <section className="border-y border-alif-rule bg-alif-paper px-4 py-14 small:px-10 large:px-14">
        <div className="mx-auto grid max-w-[1320px] gap-8 large:grid-cols-3">
          {specs.map(([label, items]) => (
            <article key={label as string} className="border-t border-alif-rule pt-5">
              <div className="flex items-center gap-3 text-alif-gold">
                <StarOrnament className="h-4 w-4" />
                <h2 className="text-[10px] font-semibold uppercase tracking-[0.25em]">
                  {label as string}
                </h2>
              </div>
              <ul className="mt-5 divide-y divide-alif-rule">
                {(items as string[]).map((item) => (
                  <li key={item} className="py-3 font-display text-[17px] text-alif-ink">
                    {item}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>
      ) : null}

      {hasStorySection ? (
      <section className="bg-[#EEF3EC] px-4 py-16 small:px-10 large:px-14">
        <div className="mx-auto max-w-[1320px]">
          <div className="mx-auto max-w-3xl text-center">
            {/* The ﷽ (U+FDFD) Bismillah ligature renders extremely wide — at
                text-5xl it is ~549px and overflowed the mobile viewport. Scale
                it down on small screens and clip as a safety net so it can never
                push the page width past the viewport. */}
            <div className="mx-auto max-w-full overflow-hidden font-arabic text-[26px] leading-none text-alif-gold xsmall:text-4xl small:text-5xl">
              ﷽
            </div>
            <div className="mt-5">
              <OrnamentDivider />
            </div>
            {storyTitle || storyAccent ? (
              <h2 className="mt-8 font-display text-[38px] leading-tight small:text-[48px]">
                {storyTitle}
                {storyAccent &&
                !storyTitle.toLowerCase().includes(storyAccent.toLowerCase()) ? (
                  <span className="italic text-alif-green"> {storyAccent}</span>
                ) : null}
              </h2>
            ) : null}
          </div>
          <div className="mt-12 grid gap-8 large:grid-cols-[5fr_4fr]">
            <div className="relative min-h-[420px] bg-alif-ivory-deep">
              {displayImages[1]?.url || displayImages[0]?.url ? (
                <Image
                  src={(displayImages[1] || displayImages[0]).url!}
                  alt={`${displayTitle} detail`}
                  fill
                  sizes="(max-width: 1024px) 100vw, 760px"
                  className="object-cover object-center"
                />
              ) : null}
              {product.collection?.title ? (
                <div className="absolute bottom-0 left-0 bg-alif-ink px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-alif-paper">
                  {product.collection.title}
                </div>
              ) : null}
            </div>
            {storyItems.length ? (
            <div className="grid content-center gap-8">
              {storyItems.map((item, index) => (
                <article key={`${item.heading}-${item.body}`} className="border-t border-alif-rule pt-5">
                  <p className="font-display text-4xl italic text-alif-green">
                    {String(index + 1).padStart(2, "0")}
                  </p>
                  <h3 className="mt-3 text-[11px] font-semibold uppercase tracking-[0.25em]">
                    {item.heading}
                  </h3>
                  <p className="mt-3 font-display text-xl leading-8 text-alif-muted">
                    {item.body}
                  </p>
                </article>
              ))}
            </div>
            ) : null}
          </div>
        </div>
      </section>
      ) : null}

      <section className="bg-[#EEF3EC] px-4 py-16 small:px-10 large:px-14">
        <div className="mx-auto grid max-w-[1320px] gap-10 large:grid-cols-[1fr_1.3fr]">
          <div>
            <Pill>Find in store</Pill>
            <h2 className="mt-6 font-display text-[40px] leading-tight">
              Available across{" "}
              <span className="italic text-alif-green">
                {storeRows.length} {storeRows.length === 1 ? "outlet" : "outlets"}
              </span>
            </h2>
            <p className="mt-5 max-w-lg text-sm leading-7 text-alif-muted">
              Choose a size to check outlet location, contact, hours, and current
              showroom stock.
            </p>
            <button
              type="button"
              onClick={() => setStoreOpen(true)}
              className="mt-8 bg-alif-ink px-6 py-4 text-[12px] font-semibold uppercase tracking-[0.22em] text-alif-paper"
            >
              Reserve for fitting
            </button>
          </div>
          <div className="border border-[#5C6B53] bg-white">
            {storeRows.map((store) => (
              <button
                type="button"
                key={store.code}
                onClick={() => setStoreOpen(true)}
                className="grid w-full grid-cols-[1fr_auto_auto] items-center gap-4 border-b border-[#5C6B53]/20 px-5 py-5 text-left last:border-b-0"
              >
                <span>
                  <span className="block font-display text-xl">{store.name}</span>
                  <span className="mt-1 block text-[11px] uppercase tracking-[0.16em] text-alif-muted">
                    {store.address || "Outlet location"}
                  </span>
                </span>
                <span
                  className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${storeBadgeClass(
                    store.status
                  )}`}
                >
                  {store.label}
                </span>
                <ArrowIcon className="h-4 w-4 text-alif-green" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {hasReviewSummary ? (
      <section className="bg-alif-paper px-4 py-16 text-center small:px-10 large:px-14">
        <Pill>Worn & reviewed</Pill>
        <div className="mx-auto mt-6 max-w-4xl">
          <p className="font-display text-7xl text-alif-ink">
            {reviewAverage?.toFixed(1)}
          </p>
          <div className="mt-3 flex justify-center gap-1 text-alif-gold">
            {Array.from({ length: 5 }).map((_, index) => (
              <StarIcon key={index} className="h-5 w-5" />
            ))}
          </div>
          <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.25em] text-alif-muted">
            {reviewCount} verified reviews
          </p>
        </div>
        {reviewHighlights.length ? (
        <div className="mx-auto mt-10 grid max-w-[1320px] gap-5 large:grid-cols-3">
          {reviewHighlights.slice(0, 3).map((review, index) => (
            <article key={review} className="bg-alif-ivory p-6 text-left">
              <div className="flex gap-1 text-alif-gold">
                {Array.from({ length: 5 }).map((_, index) => (
                  <StarIcon key={index} className="h-4 w-4" />
                ))}
              </div>
              <p className="mt-5 font-display text-[19px] italic leading-8">
                “{review}”
              </p>
              <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.22em] text-alif-muted">
                Verified review {index + 1}
              </p>
            </article>
          ))}
        </div>
        ) : null}
      </section>
      ) : null}

      {relatedCards.length ? (
      <section className="bg-alif-ivory px-4 py-16 small:px-10 large:px-14">
        <div className="mx-auto max-w-[1320px]">
          <h2 className="font-display text-4xl">You may also like</h2>
          <div className="mt-8 grid grid-cols-2 gap-x-4 gap-y-8 small:grid-cols-3 large:grid-cols-5">
            {relatedCards.map((related, index) => {
              const relatedCp = getProductPrice({ product: related }).cheapestPrice
              const relatedPrice = relatedCp?.calculated_price || ""
              const relatedSale =
                !!relatedCp &&
                relatedCp.price_type === "sale" &&
                relatedCp.original_price_number > relatedCp.calculated_price_number
              const relatedImage =
                related.thumbnail ||
                related.images?.[0]?.url
              const relatedStyle = getProductStyle(related)

              return (
                <LocalizedClientLink
                  key={`${related.id || product.id}-${index}`}
                  href={
                    related.handle
                      ? `/products/${related.handle}`
                      : `/products/${product.handle}`
                  }
                  className="group block"
                >
                  <div className="relative aspect-[3/4] bg-alif-ivory-deep">
                    {relatedImage ? (
                      <Image
                        src={relatedImage}
                        alt={getDisplayTitle(related)}
                        fill
                        optimize
                        sizes="(max-width: 1024px) 50vw, 280px"
                        className="object-cover object-center transition duration-200 group-hover:opacity-90"
                      />
                    ) : null}
                    {relatedSale && (
                      <SaleTag className="absolute right-3 top-3" />
                    )}
                  </div>
                  <h3 className="mt-4 font-display text-lg leading-6">
                    {getDisplayTitle(related)}
                  </h3>
                  {relatedStyle ? (
                    <p className="mt-1 font-sans text-[10px] uppercase tracking-[2px] text-alif-muted">
                      Style № {relatedStyle}
                    </p>
                  ) : null}
                  {relatedPrice ? (
                    <p className="mt-1 flex items-center gap-2 text-[12px]">
                      <span className={relatedSale ? "text-[#c0392b]" : "text-alif-muted"}>
                        {relatedPrice}
                      </span>
                      {relatedSale && (
                        <span className="text-alif-muted line-through">
                          {relatedCp!.original_price}
                        </span>
                      )}
                    </p>
                  ) : null}
                </LocalizedClientLink>
              )
            })}
          </div>
        </div>
      </section>
      ) : null}

      {stickyVisible ? (
        <div className={`fixed inset-x-0 ${MOBILE_PRODUCT_STICKY_BOTTOM_CLASS} z-[70] border-t border-alif-rule bg-alif-paper shadow-alif-sticky-atc`}>
          <div className="mx-auto max-w-[1320px] px-4 small:px-8">
            {/* Mobile size picker — desktop has its own inline selector below. */}
            {sizeOption ? (
              <div className="flex gap-2 overflow-x-auto border-b border-alif-rule py-2 small:hidden">
                {sortOptionValues(
                  sizeOption,
                  getVariantOptionValues(product.variants || [], sizeOption.id)
                ).map((value) => {
                  const nextOptions = { ...options, [sizeOption.id]: value }
                  const variant = variantForOptions(nextOptions)
                  const unavailable = isOutOfStockVariant(variant)
                  const active = normalize(selectedSize) === normalize(value)

                  return (
                    <button
                      type="button"
                      key={value}
                      disabled={!variant}
                      onClick={() => setOptionValue(sizeOption.id, value)}
                      aria-pressed={active}
                      aria-label={`Size ${value}${unavailable ? " out of stock" : ""}`}
                      title={unavailable ? `${value} is out of stock` : undefined}
                      className={`relative min-h-[36px] min-w-[40px] shrink-0 overflow-hidden rounded-sm border px-3 text-[12px] font-semibold uppercase tracking-[0.08em] transition disabled:cursor-not-allowed disabled:opacity-40 ${
                        active
                          ? "border-alif-ink bg-alif-ink text-alif-paper"
                          : "border-alif-rule text-alif-ink"
                      }`}
                    >
                      <span className="relative z-10">{value}</span>
                      {unavailable ? (
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute left-1/2 top-1/2 h-px w-[150%] -translate-x-1/2 -translate-y-1/2 -rotate-[24deg] bg-current opacity-60"
                        />
                      ) : null}
                    </button>
                  )
                })}
              </div>
            ) : null}
          <div className="grid grid-cols-[48px_1fr_auto] items-center gap-3 py-3 small:grid-cols-[56px_1fr_minmax(220px,360px)_auto_auto]">
            <div className="relative h-12 w-12 bg-alif-ivory-deep small:h-14 small:w-14">
              {displayImages[0]?.url ? (
                <Image
                  src={displayImages[0].url}
                  alt={displayTitle}
                  fill
                  optimize
                  sizes="56px"
                  className="object-cover object-center"
                />
              ) : null}
            </div>
            <div className="min-w-0">
              <p className="truncate font-display text-lg">{displayTitle}</p>
              <p className="mt-0.5 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-alif-muted">
                <span>Size {selectedSize}</span>
                {price ? (
                  <span className="font-semibold text-alif-ink small:hidden">
                    {price}
                  </span>
                ) : null}
              </p>
            </div>
            {sizeOption ? (
              <div className="hidden border border-alif-rule small:flex">
                {sortOptionValues(
                  sizeOption,
                  getVariantOptionValues(product.variants || [], sizeOption.id)
                )
                  .slice(0, 6)
                  .map((value) => {
                    const nextOptions = { ...options, [sizeOption.id]: value }
                    const variant = variantForOptions(nextOptions)
                    const unavailable = isOutOfStockVariant(variant)
                    const active = normalize(selectedSize) === normalize(value)

                    return (
                      <button
                        type="button"
                        key={value}
                        disabled={!variant}
                        onClick={() => setOptionValue(sizeOption.id, value)}
                        aria-label={`${value}${unavailable ? " out of stock" : ""}`}
                        title={unavailable ? `${value} is out of stock` : undefined}
                        className={`relative overflow-hidden px-3 py-2 text-[10px] font-semibold uppercase disabled:cursor-not-allowed disabled:bg-alif-rule disabled:text-alif-muted ${
                          active
                            ? "bg-alif-ink text-alif-paper"
                            : unavailable
                              ? "text-alif-muted"
                              : "text-alif-ink"
                        }`}
                      >
                        <span className="relative z-10">{value}</span>
                        {unavailable ? (
                          <span
                            aria-hidden="true"
                            className="pointer-events-none absolute left-1/2 top-1/2 h-px w-[140%] -translate-x-1/2 -translate-y-1/2 -rotate-[28deg] bg-current opacity-70"
                          />
                        ) : null}
                      </button>
                    )
                  })}
              </div>
            ) : null}
            <p className="hidden font-display text-2xl small:block">{price}</p>
            <button
              type="button"
              onClick={() => handleSubmit(false)}
              disabled={!canSubmit}
              className="bg-alif-ink px-4 py-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-alif-paper disabled:opacity-50 small:px-6"
            >
              Add to bag
            </button>
          </div>
          </div>
        </div>
      ) : null}

      <ModalShell
        open={sizeGuideOpen}
        onClose={() => setSizeGuideOpen(false)}
        title={sizeChart?.name || "Size guide"}
      >
        {sizeChart ? (
          <div className="space-y-5 p-5">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span className="inline-flex items-center gap-2 rounded-full border border-alif-rule px-3 py-1.5 text-[11px] font-medium text-alif-muted">
                <RulerIcon className="h-3.5 w-3.5 text-alif-green" />
                Measurements in {sizeChart.unit || "inch"}
              </span>
              {selectedSize ? (
                <span className="text-[12px] text-alif-muted">
                  Your size:{" "}
                  <span className="font-semibold text-alif-ink">
                    {selectedSize}
                  </span>
                </span>
              ) : null}
            </div>

            {sizeChart.description ? (
              <p className="text-sm leading-7 text-alif-muted">
                {sizeChart.description}
              </p>
            ) : null}

            {/* Measurement table — the selected size row is highlighted. The
                first (label) column stays left-aligned and the row scrolls
                horizontally on narrow screens. */}
            <div className="-mx-1 overflow-x-auto">
              <table className="w-full min-w-[360px] border-collapse text-[13px]">
                <thead>
                  <tr className="bg-alif-ivory-deep">
                    {sizeGuideColumns.map((column) => (
                      <th
                        key={column.key}
                        className="whitespace-nowrap px-3 py-3 text-center font-semibold text-alif-ink first:rounded-l-sm first:pl-4 first:text-left last:rounded-r-sm"
                      >
                        {column.label}
                        {column.type === "measurement" ? (
                          <span className="font-normal text-alif-muted">
                            {" "}
                            ({sizeChart.unit || "inch"})
                          </span>
                        ) : null}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sizeChart.rows.map((row, index) => {
                    const rowLabel = String(
                      row[sizeGuideLabelColumn?.key || "size"] ?? ""
                    )
                    const isSelected =
                      !!selectedSize &&
                      normalize(rowLabel) === normalize(selectedSize)
                    return (
                      <tr
                        key={`${rowLabel}-${index}`}
                        className={isSelected ? "bg-alif-green-soft" : ""}
                      >
                        {sizeGuideColumns.map((column) => (
                          <td
                            key={column.key}
                            className={`whitespace-nowrap border-t border-alif-rule px-3 py-3 text-center first:pl-4 first:text-left ${
                              column.type === "label" ? "font-semibold" : ""
                            } ${
                              isSelected
                                ? "text-alif-green-deep"
                                : "text-alif-ink"
                            }`}
                          >
                            {column.type === "measurement"
                              ? formatInlineMeasurement(
                                  String(row[column.key] ?? "-"),
                                  sizeChart.unit || "inch"
                                )
                              : String(row[column.key] ?? "-")}
                          </td>
                        ))}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {sizeChart.how_to_measure ? (
              <p className="text-[13px] leading-6 text-alif-muted">
                <span className="font-semibold text-alif-ink">
                  How to measure:
                </span>{" "}
                {sizeChart.how_to_measure}
              </p>
            ) : null}

            {sizeChart.fit_guide ? (
              <div className="rounded-sm border border-alif-rule bg-alif-ivory px-4 py-3 text-[13px] leading-6 text-alif-muted">
                <span className="font-semibold text-alif-ink">Fit note:</span>{" "}
                {sizeChart.fit_guide}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="p-5 text-sm leading-7 text-alif-muted">
            Size chart is not assigned for this product yet.
          </div>
        )}
      </ModalShell>

      <ModalShell
        open={storeOpen}
        onClose={() => setStoreOpen(false)}
        title="Store availability"
      >
        <div>
          {/* Product header — main thumbnail + style code + price */}
          <div className="flex items-center gap-4 border-b border-alif-rule px-5 py-4">
            <div className="relative h-[88px] w-[70px] shrink-0 overflow-hidden rounded-sm bg-alif-ivory-deep">
              {displayImages[0]?.url ? (
                <Image
                  src={displayImages[0].url}
                  alt={displayTitle}
                  fill
                  optimize
                  sizes="70px"
                  className="object-cover object-center"
                />
              ) : null}
            </div>
            <div className="min-w-0">
              <p className="font-display text-lg leading-tight text-alif-ink">
                {displayTitle}
              </p>
              <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-alif-muted">
                Style № {productCode}
              </p>
              {price ? (
                <p className="mt-1 font-display text-lg text-alif-ink">{price}</p>
              ) : null}
            </div>
          </div>

          {/* In-modal size selector — change size to check stock per size */}
          {sizeOption ? (
            <div className="border-b border-alif-rule px-5 py-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-alif-muted">
                Choose size{" "}
                <span className="text-alif-ink">{selectedSize}</span>
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {sortOptionValues(
                  sizeOption,
                  getVariantOptionValues(product.variants || [], sizeOption.id)
                ).map((value) => {
                  const active = normalize(selectedSize) === normalize(value)
                  const nextOptions = { ...options, [sizeOption.id]: value }
                  const variant = variantForOptions(nextOptions)
                  const unavailable = isOutOfStockVariant(variant)
                  const disabled = !variant

                  return (
                    <button
                      type="button"
                      key={value}
                      disabled={disabled}
                      onClick={() => setOptionValue(sizeOption.id, value)}
                      aria-pressed={active}
                      aria-label={`${value}${unavailable ? " out of stock" : ""}`}
                      title={unavailable ? `${value} is out of stock` : undefined}
                      className={`relative min-w-[46px] overflow-hidden rounded-sm border px-3 py-2.5 text-[12px] font-semibold uppercase tracking-[0.1em] transition disabled:cursor-not-allowed disabled:opacity-40 ${
                        active
                          ? "border-alif-ink bg-alif-ink text-alif-paper"
                          : "border-alif-rule text-alif-ink hover:border-alif-green hover:text-alif-green"
                      }`}
                    >
                      <span className="relative z-10">{value}</span>
                      {unavailable ? (
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute left-1/2 top-1/2 h-px w-[150%] -translate-x-1/2 -translate-y-1/2 -rotate-[24deg] bg-current opacity-60"
                        />
                      ) : null}
                    </button>
                  )
                })}
              </div>
              <p className="mt-3 text-[12px] text-alif-muted">
                Showroom availability for size{" "}
                <span className="font-semibold text-alif-ink">
                  {selectedSize}
                </span>
                .
              </p>
            </div>
          ) : null}

          {/* Per-store stock for the selected size */}
          <div className="divide-y divide-alif-rule">
          {storeRows.map((store) => (
            <div key={store.code} className="grid gap-4 px-5 py-4 small:grid-cols-[1fr_auto]">
              <div className="flex items-start gap-3">
                <PinIcon className="mt-1 h-4 w-4 text-alif-green" />
                <div>
                  <p className="font-display text-xl">{store.name}</p>
                  {store.address ? (
                    <p className="mt-1 text-sm leading-6 text-alif-muted">
                      {store.address}
                    </p>
                  ) : null}
                  {store.hours ? (
                    <p className="mt-1 text-[11px] uppercase tracking-[0.16em] text-alif-muted">
                      {store.hours}
                    </p>
                  ) : null}
                  {store.phone ? (
                    <a
                      href={`tel:${store.phone.replace(/\s+/g, "")}`}
                      className="mt-2 block text-sm font-semibold text-alif-ink underline underline-offset-4"
                    >
                      {store.phone}
                    </a>
                  ) : null}
                </div>
              </div>
              <div className="small:text-right">
                <p
                  className={`inline-flex rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${storeBadgeClass(
                    store.status
                  )}`}
                >
                  {store.label}
                </p>
                <p className="mt-2 text-sm font-semibold text-alif-ink">
                  {store.detail}
                </p>
              </div>
            </div>
          ))}
          </div>
        </div>
      </ModalShell>


      {lightboxOpen ? (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/95 p-4"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center border border-white/25 text-white transition hover:border-alif-green hover:text-alif-green"
            aria-label="Close lightbox"
          >
            <CloseIcon />
          </button>
          {displayImages.length > 1 && imageIndex > 0 ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                setImageIndex((current) => current - 1)
              }}
              className="absolute left-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center border border-white/25 text-3xl text-white transition hover:border-alif-green hover:text-alif-green"
              aria-label="Previous image"
            >
              ‹
            </button>
          ) : null}
          {displayImages.length > 1 && imageIndex < displayImages.length - 1 ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                setImageIndex((current) => current + 1)
              }}
              className="absolute right-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center border border-white/25 text-3xl text-white transition hover:border-alif-green hover:text-alif-green"
              aria-label="Next image"
            >
              ›
            </button>
          ) : null}
          <div
            className="relative h-[86vh] w-[88vw]"
            onClick={(event) => event.stopPropagation()}
          >
            {displayImages[imageIndex]?.url ? (
              <Image
                src={displayImages[imageIndex].url}
                alt={`${displayTitle} full view`}
                fill
                priority
                sizes="88vw"
                className="object-contain object-center"
              />
            ) : null}
          </div>
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-4 py-2 text-sm text-white">
            {imageIndex + 1} / {Math.max(displayImages.length, 1)}
          </div>
        </div>
      ) : null}
    </section>
  )
}
